import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import type { SessionAdapter } from './session-adapter';
import type { LruTracker } from './lru-tracker';
import type { SessionState, TabType } from './types';
import { NAMED_VIEWS } from './types';
import { setSessionOpenTab, drainSessionBridge } from './session-bridge';
import {
  openTab as reducerOpenTab,
  closeTab as reducerCloseTab,
  moveTab as reducerMoveTab,
  reorderTab as reducerReorderTab,
  focusPanel as reducerFocusPanel,
  openPanel as reducerOpenPanel,
  closePanel as reducerClosePanel,
  validateAndPrune as reducerValidateAndPrune,
  closeEntityAcrossAllPanels as reducerCloseEntityAcrossAllPanels,
  setTabSurface as reducerSetTabSurface,
} from './session-reducer';
import { capture } from '@/lib/analytics/capture';
import { useEntityStore } from '@/lib/sync/entity-store';

// ---------------------------------------------------------------------------
// Context value type
// ---------------------------------------------------------------------------

interface SessionContextValue {
  state: SessionState | null;
  openTab(entityId: string, tabType?: TabType, panelId?: string): void;
  closeTab(entityId: string, panelId: string): void;
  moveTab(entityId: string, fromPanelId: string, toPanelId: string, atIndex?: number): void;
  reorderTab(entityId: string, panelId: string, newIndex: number): void;
  focusPanel(panelId: string): void;
  openPanel(direction: 'h' | 'v', initialDocId?: string): void;
  closePanel(panelId: string): void;
  validateAndPrune(existingDocIds: Set<string>): void;
  setPanelSizes(sizes: Record<string, number>): void;
  /** Remember which surface this entity's tab is showing, so a restored
   *  session reopens the page the user left. */
  setTabSurface(entityId: string, surfaceId: string | null): void;
}

const SessionCtx = createContext<SessionContextValue | null>(null);

// ---------------------------------------------------------------------------
// Default state factory
// ---------------------------------------------------------------------------

function makeDefaultState(): SessionState {
  const panelId = crypto.randomUUID();
  return {
    layout: { type: 'panel', panelId },
    panels: { [panelId]: { panelId, activeId: 'home', tabs: [{ id: 'home', type: 'view' }] } },
    focusedPanelId: panelId,
  };
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: 'LOADED'; state: SessionState }
  | { type: 'OPEN_TAB'; entityId: string; tabType: TabType; panelId?: string }
  | { type: 'CLOSE_TAB'; entityId: string; panelId: string }
  | { type: 'MOVE_TAB'; entityId: string; fromPanelId: string; toPanelId: string; atIndex?: number }
  | { type: 'REORDER_TAB'; entityId: string; panelId: string; newIndex: number }
  | { type: 'FOCUS_PANEL'; panelId: string }
  | { type: 'OPEN_PANEL'; direction: 'h' | 'v'; initialDocId?: string }
  | { type: 'CLOSE_PANEL'; panelId: string }
  | { type: 'VALIDATE_AND_PRUNE'; existingDocIds: Set<string> }
  | { type: 'SET_PANEL_SIZES'; sizes: Record<string, number> }
  | { type: 'CLOSE_ENTITY'; entityId: string }
  | { type: 'SET_TAB_SURFACE'; entityId: string; surfaceId: string | null };

function reducer(state: SessionState | null, action: Action): SessionState | null {
  if (action.type === 'LOADED') return action.state;
  if (!state) return state;

  switch (action.type) {
    case 'OPEN_TAB':
      return reducerOpenTab(state, action.entityId, action.tabType, action.panelId);
    case 'CLOSE_TAB':
      return reducerCloseTab(state, action.entityId, action.panelId);
    case 'MOVE_TAB':
      return reducerMoveTab(state, action.entityId, action.fromPanelId, action.toPanelId, action.atIndex);
    case 'REORDER_TAB':
      return reducerReorderTab(state, action.entityId, action.panelId, action.newIndex);
    case 'FOCUS_PANEL':
      return reducerFocusPanel(state, action.panelId);
    case 'OPEN_PANEL':
      return reducerOpenPanel(state, action.direction, action.initialDocId);
    case 'CLOSE_PANEL':
      return reducerClosePanel(state, action.panelId);
    case 'VALIDATE_AND_PRUNE':
      return reducerValidateAndPrune(state, action.existingDocIds);
    case 'SET_PANEL_SIZES':
      return { ...state, panelSizes: { ...(state.panelSizes ?? {}), ...action.sizes } };
    case 'CLOSE_ENTITY':
      return reducerCloseEntityAcrossAllPanels(state, action.entityId);
    case 'SET_TAB_SURFACE':
      return reducerSetTabSurface(state, action.entityId, action.surfaceId);
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface SessionProviderProps {
  adapter: SessionAdapter;
  lruTracker: LruTracker;
  children: React.ReactNode;
}

export function SessionProvider({ adapter, lruTracker, children }: SessionProviderProps) {
  const [state, dispatch] = useReducer(reducer, null);
  // Keep a ref to current state so we can pass it to adapter.save() after dispatch
  const stateRef = useRef<SessionState | null>(state);
  stateRef.current = state;

  // Load on mount — drain any queued deep-link calls into the initial state
  useEffect(() => {
    let cancelled = false;
    adapter.load().then((loaded) => {
      if (cancelled) return;
      let initial = loaded ?? makeDefaultState();
      const pending = drainSessionBridge();
      for (const call of pending) {
        const resolvedType: TabType = call.type ?? (NAMED_VIEWS.has(call.id as 'home' | 'documents' | 'collections' | 'trash') ? 'view' : 'document');
        initial = reducerOpenTab(initial, call.id, resolvedType, undefined);
      }
      dispatch({ type: 'LOADED', state: initial });
    });
    return () => { cancelled = true; };
  }, [adapter]);

  // Stable refs for adapter and lruTracker so callbacks don't change identity
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;
  const lruRef = useRef(lruTracker);
  lruRef.current = lruTracker;

  // Stable dispatch + save — never changes identity
  const dispatchAndSave = useCallback((action: Action): void => {
    dispatch(action);
    const nextState = reducer(stateRef.current, action);
    if (nextState) adapterRef.current.save(nextState);
  }, []);

  // All actions are stable (useCallback with [] deps) — safe in useEffect deps
  const openTab = useCallback((entityId: string, tabType?: TabType, panelId?: string) => {
    lruRef.current.touch(entityId);
    const resolvedType: TabType = tabType ?? (NAMED_VIEWS.has(entityId as 'home' | 'documents' | 'collections' | 'trash') ? 'view' : 'document');
    dispatchAndSave({ type: 'OPEN_TAB', entityId, tabType: resolvedType, panelId });
    capture('tab_opened', { tab_type: resolvedType });
  }, [dispatchAndSave]);

  // Wire the imperative bridge so non-React code (Lexical nodes, stores) can open tabs
  useEffect(() => { setSessionOpenTab(openTab); }, [openTab]);

  const closeTab = useCallback((entityId: string, panelId: string) => {
    dispatchAndSave({ type: 'CLOSE_TAB', entityId, panelId });
    capture('tab_closed', {});
  }, [dispatchAndSave]);

  const moveTab = useCallback((entityId: string, fromPanelId: string, toPanelId: string, atIndex?: number) => {
    dispatchAndSave({ type: 'MOVE_TAB', entityId, fromPanelId, toPanelId, atIndex });
  }, [dispatchAndSave]);

  const reorderTab = useCallback((entityId: string, panelId: string, newIndex: number) => {
    dispatchAndSave({ type: 'REORDER_TAB', entityId, panelId, newIndex });
  }, [dispatchAndSave]);

  const focusPanel = useCallback((panelId: string) => {
    dispatchAndSave({ type: 'FOCUS_PANEL', panelId });
  }, [dispatchAndSave]);

  const openPanel = useCallback((direction: 'h' | 'v', initialDocId?: string) => {
    dispatchAndSave({ type: 'OPEN_PANEL', direction, initialDocId });
  }, [dispatchAndSave]);

  const closePanel = useCallback((panelId: string) => {
    dispatchAndSave({ type: 'CLOSE_PANEL', panelId });
  }, [dispatchAndSave]);

  const validateAndPrune = useCallback((existingDocIds: Set<string>) => {
    dispatchAndSave({ type: 'VALIDATE_AND_PRUNE', existingDocIds });
  }, [dispatchAndSave]);

  const closeEntityTabs = useCallback((entityId: string) => {
    dispatchAndSave({ type: 'CLOSE_ENTITY', entityId });
  }, [dispatchAndSave]);

  // Register with the entity store so applyEvent auto-closes tabs on
  // delete/archive without per-call-site closeTab() scatter.
  useEffect(() => {
    useEntityStore.getState().setEntityRemovedCallback(closeEntityTabs);
  }, [closeEntityTabs]);

  const setPanelSizes = useCallback((sizes: Record<string, number>) => {
    dispatchAndSave({ type: 'SET_PANEL_SIZES', sizes });
  }, [dispatchAndSave]);

  const setTabSurface = useCallback((entityId: string, surfaceId: string | null) => {
    dispatchAndSave({ type: 'SET_TAB_SURFACE', entityId, surfaceId });
  }, [dispatchAndSave]);

  const value: SessionContextValue = {
    state, openTab, closeTab, moveTab, reorderTab,
    focusPanel, openPanel, closePanel, validateAndPrune, setPanelSizes, setTabSurface,
  };

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSessionState(): SessionContextValue {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error('useSessionState must be used inside SessionProvider');
  return ctx;
}

/** Returns null instead of throwing when outside SessionProvider (e.g. share mode). */
export function useSessionStateOptional(): SessionContextValue | null {
  return useContext(SessionCtx);
}
